import type { ReactNode } from 'react'

import { cn } from 'cn'

import { Skeleton } from '#ui/components/skeleton.tsx'
import { CARD_TABLE_EDGES, truncatedWidths } from '#ui/patterns/table-layout.ts'

export const BONE_CLASS = 'bg-foreground/10'

export function placeholderKeys(prefix: string, count: number): string[] {
  return Array.from({ length: Math.max(0, Math.floor(count)) }, (_, at) => `${prefix}-${at}`)
}

export function pick(widths: readonly string[], at: number): string {
  if (widths.length === 0) return 'w-full'
  return widths[at % widths.length] ?? 'w-full'
}

export function SkeletonRegion({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <div aria-busy="true">
      <span role="status" className="sr-only">
        Loading
      </span>
      <div aria-hidden="true" className={className}>
        {children}
      </div>
    </div>
  )
}

export function Bone({ className }: { className?: string }) {
  return <Skeleton className={cn(BONE_CLASS, className)} />
}

const LINE_BOX = {
  xs: 'h-4',
  sm: 'h-5',
  base: 'h-6',
  lead: 'h-4',
  xl: 'h-7',
  '2xl': 'h-8',
} as const
const LINE_BONE = {
  xs: 'h-2.5',
  sm: 'h-3',
  base: 'h-3.5',
  lead: 'h-3.5',
  xl: 'h-5',
  '2xl': 'h-6',
} as const

export type LineSize = keyof typeof LINE_BOX

export function Line({
  size = 'sm',
  width = 'w-full',
  className,
}: {
  size?: LineSize
  width?: string
  className?: string
}) {
  return (
    <div className={cn('flex max-w-full items-center', LINE_BOX[size], width, className)}>
      <Bone className={cn('w-full', LINE_BONE[size])} />
    </div>
  )
}

const NO_ACTIONS: readonly string[] = []

export function HeaderSkeleton({
  title = 'w-48',
  description = 'w-72',
  actions = NO_ACTIONS,
  className,
}: {
  title?: string
  description?: string | null
  actions?: readonly string[]
  className?: string
}) {
  const actionKeys = placeholderKeys('action', actions.length)
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-col gap-1">
          <Line size="xl" width={title} />
          {description === null ? null : <Line width={description} />}
        </div>
        {actions.length === 0 ? null : (
          <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">
            {actions.map((action, at) => (
              <Bone key={actionKeys[at]} className={action} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export type CellBone =
  | 'text'
  | 'muted'
  | 'mono'
  | 'badge'
  | 'two-line'
  | 'button'
  | 'select'
  | 'icon'
  | 'handle'
  | 'thumb'

export type ColumnBone = {
  cell: CellBone
  widths?: readonly string[]
  header?: string | null
  align?: 'end'
  shrink?: boolean
  primary?: boolean
  hideOnCard?: boolean
  truncate?: boolean
  size?: number
}

function CellContent({ column, at }: { column: ColumnBone; at: number }) {
  const width = pick(column.widths ?? [], at)
  switch (column.cell) {
    case 'text':
    case 'muted':
      return <Line width={width} />
    case 'mono':
      return <Line size="xs" width={width} />
    case 'badge':
      return <Bone className={cn('h-[22px]', width)} />
    case 'two-line':
      return (
        <div className="flex w-full min-w-0 flex-col">
          <Line width={width} />
          <Line size="xs" width={pick(column.widths ?? [], at + 1)} />
        </div>
      )
    case 'button':
      return <Bone className={cn('h-8', width)} />
    case 'select':
      return <Bone className="h-8 w-full min-w-40" />
    case 'icon':
      return <Bone className="size-9" />
    case 'handle':
      return <Bone className="size-11 sm:size-8" />
    case 'thumb':
      return <Bone className="size-12" />
    default: {
      const unreachable: never = column.cell
      throw new Error(`Unhandled cell: ${String(unreachable)}`)
    }
  }
}

export function TableSkeleton({
  columns,
  rows = 5,
  card = null,
  className,
}: {
  columns: readonly ColumnBone[]
  rows?: number
  card?: { description?: string | null; action?: string | null; title?: string } | null
  className?: string
}) {
  const rowKeys = placeholderKeys('row', rows)
  const columnKeys = placeholderKeys('column', columns.length)
  const primary = columns.find((column) => column.primary === true) ?? columns[0]
  const onCard = columns.filter(
    (column) => column !== primary && column.hideOnCard !== true && column.header !== null,
  )

  const widths = truncatedWidths(
    columns.map((column, at) => ({ id: String(at), truncate: column.truncate, size: column.size })),
  )

  const table = (
    <table className={cn('w-full caption-bottom text-sm', card !== null && CARD_TABLE_EDGES)}>
      <thead className="bg-muted/60">
        <tr className="border-b">
          {columns.map((column, at) => (
            <th
              key={columnKeys[at]}
              className={cn(
                'h-10 px-2 text-left align-middle',
                column.shrink === true && 'w-px',
                widths.has(String(at)) && 'max-w-0 overflow-hidden',
              )}
              style={{ width: widths.get(String(at)) }}
            >
              {column.header === null ? null : (
                <div className={cn('flex', column.align === 'end' && 'justify-end')}>
                  <Line width={column.header ?? 'w-16'} />
                </div>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="[&_tr:last-child]:border-0">
        {rowKeys.map((rowKey, row) => (
          <tr key={rowKey} className="border-b">
            {columns.map((column, at) => (
              <td
                key={columnKeys[at]}
                className={cn(
                  'p-2 align-middle',
                  column.shrink === true && 'w-px',
                  widths.has(String(at)) && 'max-w-0 overflow-hidden',
                )}
                style={{ width: widths.get(String(at)) }}
              >
                <div className={cn('flex', column.align === 'end' && 'justify-end')}>
                  <CellContent column={column} at={row + at} />
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )

  const cards = (
    <ul className={cn('flex flex-col gap-3 md:hidden', card !== null && 'p-4')}>
      {rowKeys.slice(0, 3).map((rowKey, row) => (
        <li key={rowKey} className="bg-card rounded-lg border p-4">
          <Line size="base" width={pick(primary?.widths ?? ['w-40'], row)} className="mb-3" />
          <div className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-2">
            {onCard.map((column, at) => (
              <div key={columnKeys[at]} className="contents">
                <Line width="w-16" />
                <CellContent column={column} at={row + at} />
              </div>
            ))}
          </div>
        </li>
      ))}
    </ul>
  )

  if (card === null) {
    return (
      <div className={className}>
        <div className="hidden overflow-x-auto rounded-lg border md:block">{table}</div>
        {cards}
      </div>
    )
  }

  return (
    <div className={cn('bg-card flex flex-col rounded-xl border shadow-sm', className)}>
      <CardHeaderSkeleton
        title={card.title ?? 'w-40'}
        description={card.description === undefined ? 'w-64' : card.description}
        action={card.action ?? null}
        className="border-b py-5"
      />
      <div className="hidden overflow-x-auto md:block">{table}</div>
      {cards}
    </div>
  )
}

export function CardHeaderSkeleton({
  title = 'w-32',
  description = null,
  action = null,
  className,
}: {
  title?: string
  description?: string | null
  action?: string | null
  className?: string
}) {
  return (
    <div
      className={cn(
        'grid auto-rows-min items-start gap-2 px-6',
        action !== null && 'grid-cols-[1fr_auto]',
        className,
      )}
    >
      <Line size="lead" width={title} />
      {description === null ? null : <Line width={description} />}
      {action === null ? null : (
        <Bone className={cn('col-start-2 row-span-2 row-start-1 self-start', action)} />
      )}
    </div>
  )
}

export function CardSkeleton({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('bg-card flex flex-col gap-6 rounded-xl border py-6 shadow-sm', className)}>
      {children}
    </div>
  )
}

export function PanelSkeleton({
  title = 'w-32',
  description = null,
  className,
  children,
}: {
  title?: string | null
  description?: string | null
  className?: string
  children?: ReactNode
}) {
  return (
    <div
      className={cn(
        'bg-card flex flex-col gap-4 rounded-lg border border-(--border) p-4 md:p-6',
        className,
      )}
    >
      {title === null && description === null ? null : (
        <div className="flex flex-col gap-1">
          {title === null ? null : <Line size="base" width={title} />}
          {description === null ? null : <Line width={description} />}
        </div>
      )}
      {children}
    </div>
  )
}

export type ControlBone =
  | 'input'
  | 'select'
  | 'textarea'
  | 'textarea-sm'
  | 'richtext'
  | 'richtext-tall'
  | 'checkbox'

const TOOLBAR_KEYS = placeholderKeys('tool', 6)

function RichTextBone({ tall }: { tall: boolean }) {
  return (
    <div className="flex w-full flex-col rounded-md border">
      <div className="flex flex-wrap items-center gap-1 border-b px-1.5 py-1">
        <Bone className="h-8 w-36" />
        {TOOLBAR_KEYS.map((key) => (
          <Bone key={key} className="size-8" />
        ))}
      </div>
      <div className={cn('flex flex-col gap-3 px-3 py-2', tall ? 'min-h-80' : 'min-h-32')}>
        <Line width="w-3/4" />
        <Line width="w-1/2" />
      </div>
    </div>
  )
}

function ControlContent({ control }: { control: ControlBone }) {
  switch (control) {
    case 'input':
    case 'select':
      return <Bone className="h-9 w-full" />
    case 'textarea':
      return <Bone className="h-24 w-full" />
    case 'textarea-sm':
      return <Bone className="h-16 w-full" />
    case 'richtext':
      return <RichTextBone tall={false} />
    case 'richtext-tall':
      return <RichTextBone tall />
    case 'checkbox':
      return null
    default: {
      const unreachable: never = control
      throw new Error(`Unhandled control: ${String(unreachable)}`)
    }
  }
}

export function FieldSkeleton({
  control = 'input',
  label = 'w-24',
  help = null,
  className,
}: {
  control?: ControlBone
  label?: string
  help?: string | null
  className?: string
}) {
  if (control === 'checkbox') {
    return (
      <div className={cn('flex min-w-0 items-start gap-3 py-1', className)}>
        <Bone className="mt-0.5 size-4 shrink-0" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Line width={label} />
          {help === null ? null : <Line size="xs" width={help} />}
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)}>
      <Line size="xs" width={label} className="h-3.5" />
      <ControlContent control={control} />
      {help === null ? null : <Line size="xs" width={help} />}
    </div>
  )
}

export function LinkTabsSkeleton({ count, className }: { count: number; className?: string }) {
  return (
    <div className={cn('overflow-hidden', className)}>
      <ul className="flex min-w-max gap-1">
        {placeholderKeys('tab', count).map((key) => (
          <li
            key={key}
            className="inline-flex min-h-10 items-center border-b-2 border-transparent px-3"
          >
            <Line width="w-16" />
          </li>
        ))}
      </ul>
    </div>
  )
}

export function MeterSkeleton() {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <Line size="xs" width="w-24" />
        <Line size="xs" width="w-16" />
      </div>
      <Bone className="h-1.5 w-full rounded-full" />
    </div>
  )
}
